import { createContext } from "react";

import type { IButton } from "@Components/Button";
import { Button } from "@Components/Button";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { Loading } from "@Components/Loading";
import { requestTrack, urlFormat } from "@Components/Modules/Helpers";
import { isNullOrEmpty } from "@Components/Modules/Validations";
import type { ISearchBox, ISearchBoxResultItem } from "@Components/SearchBox";
import { SearchBox } from "@Components/SearchBox";

import type { ICategoriesCarousel, ICategoriesCarouselItem } from "../common/CategoriesCarousel";
import { CategoriesCarousel } from "../common/CategoriesCarousel";
import type { ILogoTile } from "../common/LogoTile";
import type { IBrandByCategory, IBrandByCategoryResult, IBrandIdFollowResult } from "./BrandsByCategory";
import { BrandsByCategory, BrandsByCategoryBase } from "./BrandsByCategory";

import * as css from "./BrandsList.module.css";

export interface IBrandsList extends IComponentAjax<IBrandsListResult> {
    allBrands: IButton;
    title: string;
    description: string;
    brandSearch: ISearchBox;
    brandsByCategoryList: IBrandByCategory[];
    categoriesCarousel: ICategoriesCarousel;
    showCategoriesCarousel: boolean;
    itemsNumber?: number;
    itemsNumberTablet?: number;
    updateOnChange?: boolean;
    searchTrackerAction?: string;
}

export interface IBrandsListState extends IComponentAjaxState {
    isLoading: boolean;
    brandsByCategoryList: IBrandByCategory[] | null;
    brandNameInput: string;
    brandSearchValue: string;
    selectedCategoryName: string;
    selectedCategoryIndex: number;
    itemToUpdate: IBrandIdFollowResult | null;
    categoriesCarousel: ICategoriesCarousel;
}

interface IBrandsListCategoriesCarouselItem extends ICategoriesCarouselItem {
    position: number;
}

interface IBrandByCategoryItem extends IBrandByCategory {
    position: number;
}

type IBrandsListResult = Array<ICategoriesCarouselItem>;

export const BrandsListContext = createContext<{
    updateList: (id: string, active: boolean) => void;
}>({
    updateList: () => {},
});

export class BrandsList extends ComponentAjax<IBrandsListResult, IBrandsList, IBrandsListState> {
    public static defaultProps: IBrandsList = {
        ...ComponentAjax.defaultProps,
        allBrands: null,
        title: undefined,
        description: undefined,
        brandSearch: null,
        brandsByCategoryList: null,
        categoriesCarousel: null,
        showCategoriesCarousel: true,
        searchTrackerAction: null,
    };

    private searchTimeout: number;

    constructor(props: IBrandsList) {
        super(props);

        this.state = {
            isLoading:
                (props.initialData === null && props.showCategoriesCarousel) ||
                props.brandsByCategoryList === null,
            brandsByCategoryList: props.brandsByCategoryList
                ? this.buildCategoriesItems(props.initialData)
                : null,
            brandNameInput: null,
            brandSearchValue: "",
            selectedCategoryName: "",
            selectedCategoryIndex: 0,
            itemToUpdate: null,
            categoriesCarousel: props.initialData
                ? {
                      ...props.categoriesCarousel,
                      categories: this.buildSliderItems(props.initialData),
                  }
                : null,
        };
    }

    public updateList = (id: string, active: boolean) => {
        if (!this.state) {
            return;
        }

        const list: IBrandByCategory[] = [];
        const brandCategory: ILogoTile | null = null;

        if (this.state.brandsByCategoryList) {
            this.state.brandsByCategoryList.forEach((brandsByCategory) => {
                if (
                    brandsByCategory &&
                    brandsByCategory.initialData &&
                    (brandsByCategory.categoryName === "Ulubione" ||
                        brandsByCategory.categoryName === "Favourites")
                ) {
                    let brandsByCategoryFilter = null;

                    if (active === true) {
                        brandsByCategoryFilter = {
                            ...brandsByCategory,
                            initialData: {
                                ...brandsByCategory.initialData,
                                brands: [brandCategory, ...brandsByCategory.initialData.brands],
                            },
                        };
                    } else {
                        brandsByCategoryFilter = {
                            ...brandsByCategory,
                            initialData: {
                                ...brandsByCategory.initialData,
                                brands: brandsByCategory.initialData.brands.filter(
                                    (brand) => brand.id !== id,
                                ),
                            },
                        };
                    }

                    list.push(brandsByCategoryFilter);
                } else {
                    list.push(brandsByCategory);
                }
            });

            this.setState({
                brandsByCategoryList: list,
                selectedCategoryIndex: 0,
                brandNameInput: "",
                selectedCategoryName: "",
                brandSearchValue: "",
            });
        }
    };

    public render() {
        const brandInput =
            this.state.brandNameInput === null ? this.state.brandSearchValue : this.state.brandNameInput;

        return (
            <section {...this.rootDefaultAttributes(css.brandsListComponent)}>
                {this.props.title && (
                    <header>
                        <h2>{this.props.title}</h2>
                    </header>
                )}
                <div className={css.topContainer}>
                    <div className={css.description}>{this.props.description}</div>
                    <SearchBox
                        {...this.props.brandSearch}
                        searchListMaxHeight={320}
                        onSelect={this.onSelect}
                        value={brandInput}
                        onChange={this.onValueChange}
                    />
                </div>
                {this.props.showCategoriesCarousel === false ? null : this.renderCategories()}
                {this.renderBrandsByCategory()}
                {this.state.brandSearchValue.localeCompare("") !== 0 ||
                this.state.selectedCategoryIndex !== 0 ? (
                    <Button {...this.props.allBrands} onClick={this.cleanFilters} />
                ) : null}
                {this.renderAjaxErrorContainer()}
            </section>
        );
    }

    protected ajaxSuccess(response: IBrandsListResult): void {
        this.setState({
            categoriesCarousel: {
                ...this.props.categoriesCarousel,
                categories: this.buildSliderItems(response),
            },
            brandsByCategoryList: this.buildCategoriesItems(response),
            isLoading: false,
        });
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false });
        return true;
    }

    private buildCategoriesItems = (obj: IBrandsListResult): IBrandByCategoryItem[] | null => {
        if (!obj) {
            return null;
        }

        const brandsByCategoryDefault: IBrandByCategory =
            (this.props.brandsByCategoryList && this.props.brandsByCategoryList[0]) ||
            (BrandsByCategoryBase.defaultProps as IBrandByCategory);

        return [
            null,
            ...obj.map((currentValue, i) => ({
                ...brandsByCategoryDefault,
                categoryName: currentValue.name,
                clientId: currentValue.id,
                ...currentValue,
                onDataLoad: (response: IBrandByCategoryResult) => this.onCategoryDataLoaded(response, i + 1),
                checkViewportVisible: true,
                position: i + 1,
            })),
        ];
    };

    private buildSliderItems = (obj: IBrandsListResult): IBrandsListCategoriesCarouselItem[] => {
        const category =
            this.props.categoriesCarousel &&
            this.props.categoriesCarousel.categories &&
            this.props.categoriesCarousel.categories[0];

        let sliderItems: IBrandsListCategoriesCarouselItem[] = [
            {
                ...category,
                position: 0,
                image: {
                    ...(category && category.image),
                },
            },
        ];

        sliderItems = [
            ...sliderItems,
            ...obj.map((currentValue, index) => ({
                ...category,
                ...currentValue,
                position: index + 1,
            })),
        ];

        return sliderItems;
    };

    private onChange = (obj: IBrandsListCategoriesCarouselItem): boolean => {
        this.setState({
            selectedCategoryIndex: obj.position,
            selectedCategoryName: obj.name,
        });

        return true;
    };

    private onSelect = (_event: React.MouseEvent<HTMLAnchorElement>, item: ISearchBoxResultItem): boolean => {
        if (item && item.text) {
            requestTrack(
                urlFormat(
                    this.props.searchTrackerAction,
                    { searchText: this.state.brandSearchValue || "" },
                    true,
                ),
            );
            this.setState({ brandSearchValue: item.text });
        }

        return true;
    };

    private onValueChange = (event: React.ChangeEvent<HTMLInputElement>): boolean => {
        const input = event.target.value;

        if (isNullOrEmpty(input) && this.state.brandSearchValue.localeCompare(input) !== 0) {
            window.clearTimeout(this.searchTimeout);
            this.setState({
                brandNameInput: input,
                brandSearchValue: input,
            });

            return false;
        }

        if (this.props.updateOnChange === true) {
            this.setState({ brandNameInput: input });
            window.clearTimeout(this.searchTimeout);
            this.searchTimeout = window.setTimeout(() => this.setState({ brandSearchValue: input }), 300);
            return false;
        }

        return true;
    };

    private onCategoryDataLoaded = (response: IBrandByCategoryResult, position: number): void => {
        this.setState((prevState: IBrandsListState) => {
            const { brandsByCategoryList } = prevState;
            brandsByCategoryList[position].initialData = response;
            return { brandsByCategoryList };
        });
    };

    private cleanFilters = () =>
        this.setState({ selectedCategoryIndex: 0, selectedCategoryName: "", brandSearchValue: "" });

    private renderCategories = () => {
        if (this.state.isLoading === true) {
            return <Loading />;
        }

        if (this.state.categoriesCarousel) {
            const categoriesCarousel = {
                ...this.state.categoriesCarousel,
                onClick: this.onChange,
                selectedIndex: this.state.selectedCategoryIndex,
            };

            return (
                <div className={css.brands}>
                    <CategoriesCarousel {...categoriesCarousel} />
                </div>
            );
        }

        return null;
    };

    private renderSearchResults = (): JSX.Element[] => {
        let initialData: ILogoTile[] = [];

        const idArray: { [key: string]: boolean } = {};

        if (!this.state.brandsByCategoryList) {
            return [];
        }

        this.state.brandsByCategoryList.forEach((cat) => {
            if (!cat || !cat.initialData || !cat.initialData.brands) {
                return;
            }

            cat.initialData.brands.forEach((value) => {
                if (value && value.id) {
                    if (idArray[value.id] !== true) {
                        initialData = [...initialData, value];
                        idArray[value.id] = true;
                    }
                }
            });
        });

        return [
            <BrandsByCategory
                {...(BrandsByCategoryBase.defaultProps as IBrandByCategory)}
                initialData={{ brands: initialData }}
                key="SearchResult"
                brandSearchName={this.state.brandSearchValue}
                itemsNumber={this.props.itemsNumber}
                showAll={true}
                itemsNumberTablet={this.props.itemsNumberTablet}
            />,
        ];
    };

    private renderBrandsByCategory = () => {
        if (this.state.isLoading === true) {
            return <Loading />;
        }

        if (!this.state.brandsByCategoryList) {
            return null;
        }

        if (this.state.brandSearchValue && this.props.updateOnChange) {
            return this.renderSearchResults();
        }

        let items: JSX.Element[] = [];

        for (let i = 1; i < this.state.brandsByCategoryList.length; i += 1) {
            if (this.state.selectedCategoryIndex === 0 || this.state.selectedCategoryIndex === i) {
                const brand = this.state.brandsByCategoryList[i];
                if (brand) {
                    items = [
                        ...items,
                        <BrandsByCategory
                            key={`${brand.categoryName}-${this.state.brandSearchValue}`}
                            {...brand}
                            brandSearchName={this.state.brandSearchValue}
                            itemsNumber={this.props.itemsNumber}
                            itemsNumberTablet={this.props.itemsNumberTablet}
                        />,
                    ];
                }
            }
        }
        return items;
    };
}
